/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { extend } from '@react-three/fiber';
import { FunctionComponent, memo, useRef, useLayoutEffect, useEffect } from 'react';
import { Jute } from '../../..';
import { IEntityActionTypes, IUserActions, useActionStateStore, useDataStore, useSceneStore, useUserStore } from '../../../zustand';
import { TargetImagePreviewBufferGeometry } from '@r3f/components/TargetImagePreviewBufferGeometry/TargetImagePreviewBufferGeometry';
import TargetImagePromptUI from './TargetImagePromptUI/TargetImagePromptUI'
import { useCameraIsUserFacing, useGetFirstSceneId } from '../../../hooks/general';
import { useGetActiveTrackingImageData } from '@/components/hoc/hocCustomHooks';
import { IImageSceneDict } from '@/components/Root';
import ImageTrackerGroups from './ImageTrackerGroups';
import { useGetTargetLostBehaviour } from './hooks';
import { IActionCategory, IComponentType } from '@r3f-component-data-structure';
import { getAllSceneEntityIdsByEntityType } from '@/utils';

extend({ TargetImagePreviewBufferGeometry });

type IParentProps = {
	enabled: boolean;
	children: IImageSceneDict | undefined;
};

export let targetLostTimeout: {value: ReturnType<typeof setTimeout> | undefined} = {value: undefined};

export const ImageTrackerWrapper: FunctionComponent<IParentProps> = ({ enabled, children }) => {
	const userDispatch = useUserStore(({ dispatch }) => dispatch);
	const actionDispatch = useActionStateStore(({ dispatch }) => dispatch);

	const isTrackingImageVisible = useUserStore(({ isTrackingImageVisible }) => isTrackingImageVisible);
	const isGrabAndGoActive = useUserStore(({isGrabNGoActive}) => isGrabNGoActive);
	const imageTrackingDataDict = useDataStore((state) => state.trackingImageDict) ?? {};
	const imageTrackingData = useGetActiveTrackingImageData();
	const cameraIsUserFacing = useCameraIsUserFacing(1500);
	const linkSceneEnabledImageTrackerId = useUserStore((state) => state.linkSceneEnabledImageTrackerId);
	const hasBeenVisibleRef = useRef(false);
	const enabledImageTrackerIds = useUserStore((state) => state.enabledImageTrackerIds);
	const targetLostBehaviour = useGetTargetLostBehaviour();
	const firstSceneId = useGetFirstSceneId(); 

	const hidePrompt = targetLostBehaviour?.type === IActionCategory.noAction;
	const linkSceneEnabledTrkImgUrl = linkSceneEnabledImageTrackerId ? imageTrackingDataDict[linkSceneEnabledImageTrackerId].imageUrl : undefined;
	const onlyScanEnabledTrkImgUrl = enabledImageTrackerIds.length === 1 ? imageTrackingDataDict[enabledImageTrackerIds[0]].imageUrl : undefined;
	const trkImgUrl = linkSceneEnabledTrkImgUrl || onlyScanEnabledTrkImgUrl;


	// if initially no target seen, show prompt after a second. this timeout gets
	// cleared out if target visible before a second by onVisible callback.

	// When Jute is ready, set tracking image visibility to false, unless it has been already visible
	// Delay for a bit to allow for the geometry to contruct before

	// the target lost prompt doesn't show if the first scene is not image tracked as it also requires
	// the image tracking group to be enabled


	// SHOW INITIAL TARGET LOST PROMPT OR NOT
	useLayoutEffect(() => {
		Jute.onReady(() => {
			targetLostTimeout.value = setTimeout(() => {
				if (hasBeenVisibleRef.current) return;
				userDispatch({
					type: IUserActions.SET_TRACKING_IMAGE_VISIBLE,
					payload: { visible: false },
				});
			}, !!imageTrackingData?.bottomRadius ? 1000 : 500);
		});
	}, [userDispatch]);

	const onGrabnGoClick = () => {
		const isGrabAndGoActive = useUserStore.getState().isGrabNGoActive;
		const disableGrabAndGo = useUserStore.getState().isGrabNGoEnabled;
		const activeSceneId = useSceneStore.getState().activeSceneId ?? firstSceneId;
		const componentsById = useDataStore.getState().componentsById;
		const sceneVideoIds = getAllSceneEntityIdsByEntityType(activeSceneId, componentsById, IComponentType.Video);

		userDispatch({
			type: IUserActions.SET_GRAB_N_GO_ACTIVE,
			payload: false,
		});

		if (!isGrabAndGoActive) {
			userDispatch({
				type: IUserActions.SET_GRAB_N_GO_ENABLED,
				payload: !disableGrabAndGo,
			});
		} else {
			actionDispatch?.({
				type: IEntityActionTypes.PAUSE_VIDEOS_BY_ID,
				payload: { ids: sceneVideoIds },
			});
		}
		
		if (isGrabAndGoActive || disableGrabAndGo) {
			userDispatch({
				type: IUserActions.SET_TRACKING_IMAGE_VISIBLE,
				payload: { visible: false },
			});
		}	
	}

	useEffect(() => {
		if (enabled) return;		
		userDispatch({
			type: IUserActions.SET_GRAB_N_GO_ACTIVE,
			payload: false,
		});
	}, [enabled])

	return (
		<>
			<TargetImagePromptUI forceEnabledImageUrl={trkImgUrl} onGrabnGoClick={onGrabnGoClick} isVisible={enabled && !hidePrompt && (!isTrackingImageVisible || isGrabAndGoActive) && !cameraIsUserFacing} />
			<ImageTrackerGroups 
				enabled={enabled}
				onVisible={() => {
					hasBeenVisibleRef.current = true;
					userDispatch({
						type: IUserActions.SET_GRAB_N_GO_ACTIVE,
						payload: false,
					});
				}} 
			>
				{children}
			</ImageTrackerGroups>
		</>
	);
};

ImageTrackerWrapper.displayName = 'ImageTrackerWrapper';
export default memo(ImageTrackerWrapper);
